import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";

export function QuickAdd({ product, dark = false }) {
    const { addItem } = useStore();
    const quickAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const size = product.sizes.includes("M") ? "M" : product.sizes[Math.floor(product.sizes.length / 2)];
        const color = product.colors[0];
        const variant = product.variants?.find((v) => v.size === size && v.color === color) || product.variants?.[0];
        addItem({
            product_id: product.id,
            name: product.name,
            size,
            color,
            print_type: "STANDARD",
            qty: 1,
            price: product.price,
            image: product.front,
            variant_id: variant?.id,
        });
        toast.success(`Added to bag — ${size} / ${color}`);
    };
    return (
        <button
            data-testid={`quick-add-${product.id}`}
            data-cursor="BAG"
            onClick={quickAdd}
            aria-label={`Quick add ${product.name} to bag`}
            className={`absolute bottom-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-110 active:scale-90 md:opacity-0 md:group-hover:opacity-100 ${
                dark
                    ? "border-[rgba(10,10,10,0.2)] bg-white/85 text-[#F2F0EA]"
                    : "border-[rgba(10,10,10,0.25)] bg-[rgba(10,10,10,0.45)] text-[#F2F0EA]"
            }`}
        >
            <Plus size={15} strokeWidth={2} />
        </button>
    );
}

export default function ProductCard({ product, priority = false }) {
    const [hover, setHover] = useState(false);
    return (
        <Link
            data-testid={`product-card-${product.id}`}
            data-cursor="VIEW"
            to={`/product/${product.id}`}
            className="group block"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#ECEAE4] md:rounded-none">
                <img
                    src={product.front}
                    alt={product.name}
                    loading={priority ? "eager" : "lazy"}
                    className="absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.025] group-hover:opacity-0"
                />
                {product.video && hover ? (
                    <video
                        data-testid={`product-card-video-${product.id}`}
                        src={product.video}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : (
                    <img
                        src={product.back}
                        alt={`${product.name} — back`}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover opacity-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.025] group-hover:opacity-100"
                    />
                )}
                {product.tag && (
                    <span className="absolute left-3 top-3 text-[8px] uppercase tracking-[0.3em] text-[rgba(242,240,234,0.7)]">
                        {product.tag}
                    </span>
                )}
                <QuickAdd product={product} />
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
                <h3 className="link-underline font-display text-[11px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">
                    {product.name}
                </h3>
                <span className="whitespace-nowrap text-[11px] tracking-wider text-[#8E8D8A]">${product.price} CAD</span>
            </div>
        </Link>
    );
}
